/**
 * SupplierDiscoveryPanel — auto-discovered external suppliers for a backorder.
 *
 * Shows a table of candidates that AskIsaac's web crawler found by searching
 * for the SKU/product on the open web (excluding Amazon/eBay/social/etc).
 * Each row is labeled "Possible source found" — we never claim "in stock"
 * without confirming via outreach.
 *
 * Admin can select rows and click "Send Sourcing Email" to BCC every captured
 * email at once using the same RFQ template as the standard supplier broadcast.
 */
import { useEffect, useState } from "react";
import { Search, RefreshCw, Mail, ExternalLink, Phone, CheckCircle2, Clock, UserPlus } from "lucide-react";
import { toast } from "sonner";
import SupplierIntroDialog from "./SupplierIntroDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierDiscoveryPanel({ backorder, token, onRefresh }) {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("idle");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [introLead, setIntroLead] = useState(null);
  const backorderId = backorder?.id;

  const auth = { Authorization: `Bearer ${token}` };

  const fetchLeads = async () => {
    if (!backorderId) return;
    try {
      const r = await fetch(`${API}/admin/backorders/${backorderId}/supplier-leads`, { headers: auth });
      if (r.ok) {
        const data = await r.json();
        // Sort priority: vendor_type (distributor > reseller > unknown > manufacturer > marketplace),
        // then by AI rank, then verified-first, then has-email-first.
        // Why? Manufacturers are almost always the SOURCE of the backorder
        // (e.g. 3M for SKU 9200) — they won't have stock, so they sink to the bottom.
        const vendorRank = { distributor: 5, reseller: 4, unknown: 3, marketplace: 2, manufacturer: 1 };
        const aiRank = { high: 3, likely: 2, unlikely: 1, off_topic: 0 };
        const sorted = (data.leads || []).slice().sort((a, b) => {
          const av = vendorRank[a.vendor_type] ?? 3;
          const bv = vendorRank[b.vendor_type] ?? 3;
          if (bv !== av) return bv - av;
          const ar = aiRank[a.ai_label] ?? -1;
          const br = aiRank[b.ai_label] ?? -1;
          if (br !== ar) return br - ar;
          if ((a.ai_score || 0) !== (b.ai_score || 0)) return (b.ai_score || 0) - (a.ai_score || 0);
          if (!!b.is_verified_supplier !== !!a.is_verified_supplier) return b.is_verified_supplier - a.is_verified_supplier;
          const ah = (a.emails || []).length > 0 ? 1 : 0;
          const bh = (b.emails || []).length > 0 ? 1 : 0;
          return bh - ah;
        });
        setLeads(sorted);
      }
    } catch (e) {
      console.error("fetch supplier leads failed", e);
    }
  };

  const fetchStatus = async () => {
    if (!backorderId) return;
    try {
      const r = await fetch(`${API}/admin/backorders/${backorderId}/lead-discovery-status`, { headers: auth });
      if (r.ok) {
        const data = await r.json();
        setStatus(data.status || "idle");
      }
    } catch (e) {
      console.error("fetch discovery status failed", e);
    }
  };

  useEffect(() => {
    if (!backorderId) return;
    fetchStatus();
    fetchLeads();
    // Poll while running
    const id = setInterval(() => {
      if (status === "running") {
        fetchStatus();
        fetchLeads();
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backorderId, status]);

  const triggerDiscovery = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/backorders/${backorderId}/discover-suppliers`, {
        method: "POST",
        headers: { ...auth, "Content-Type": "application/json" },
      });
      if (!r.ok) throw new Error("Failed to start discovery");
      setStatus("running");
      toast.message("Searching the web for possible suppliers… this takes 30-60 seconds.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    // Select-all targets only distributors / resellers / unknown vendors with
    // an email. Manufacturers are intentionally NOT auto-selected because they
    // are usually the source of the backorder.
    const eligible = leads.filter(l =>
      (l.emails || []).length > 0
      && l.vendor_type !== "manufacturer"
    );
    if (selectedIds.size === eligible.length && eligible.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map(l => l.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const contactSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one supplier first");
      return;
    }
    setContacting(true);
    try {
      const r = await fetch(`${API}/admin/backorders/${backorderId}/contact-leads`, {
        method: "POST",
        headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify({ lead_ids: Array.from(selectedIds) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to build email");
      }
      const data = await r.json();
      // Open the mailto link → Outlook deep-link with all BCCs pre-filled
      window.location.href = data.mailto;
      toast.success(`Opened email draft for ${data.recipient_count} supplier(s).`);
      setSelectedIds(new Set());
      // Refresh to show the "contacted" pills
      setTimeout(() => { fetchLeads(); onRefresh?.(); }, 1200);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setContacting(false);
    }
  };

  const totalEmails = leads.reduce((sum, l) => sum + (l.emails || []).length, 0);

  return (
    <section
      className="bg-white border border-cyan-200 rounded-lg p-4 shadow-sm"
      data-testid="supplier-discovery-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-semibold text-cyan-900 flex items-center gap-1.5">
            <Search className="w-4 h-4" />
            Auto-Discovered Possible Suppliers
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Web-searched, filtered, and labeled <em>possible source</em> — never &ldquo;in stock&rdquo; until they reply.
          </p>
        </div>
        <button
          onClick={triggerDiscovery}
          disabled={loading || status === "running"}
          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-cyan-300 text-cyan-800 bg-white hover:bg-cyan-50 disabled:opacity-60 inline-flex items-center gap-1.5"
          data-testid="trigger-supplier-discovery"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${status === "running" ? "animate-spin" : ""}`} />
          {status === "running" ? "Searching…" : leads.length > 0 ? "Re-run search" : "Search the web"}
        </button>
      </div>

      {status === "running" && leads.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3" data-testid="discovery-running">
          <Clock className="w-4 h-4 animate-pulse text-cyan-600" />
          Searching the web for possible suppliers. Results auto-populate below as they are found.
        </div>
      )}

      {leads.length === 0 && status !== "running" && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded p-4 text-center">
          No auto-discovered leads yet. Click <strong>Search the web</strong> to scan for possible external suppliers carrying this SKU.
        </div>
      )}

      {leads.length > 0 && (
        <>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === leads.filter(l => (l.emails || []).length > 0).length}
                      onChange={toggleAll}
                      data-testid="discovery-select-all"
                    />
                  </th>
                  <th className="p-2 text-left">Company</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Contact</th>
                  <th className="p-2 text-left">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead) => {
                  const hasEmail = (lead.emails || []).length > 0;
                  const isManufacturer = lead.vendor_type === "manufacturer";
                  const aiPill = {
                    high:     { label: "AI: High confidence", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
                    likely:   { label: "AI: Likely match",     cls: "bg-blue-100 text-blue-800 border border-blue-200" },
                    unlikely: { label: "AI: Unlikely",         cls: "bg-slate-100 text-slate-600 border border-slate-200" },
                  }[lead.ai_label] || null;
                  const vendorPill = {
                    distributor:  { label: "Distributor",                            cls: "bg-cyan-100 text-cyan-800 border border-cyan-200" },
                    reseller:     { label: "Reseller",                               cls: "bg-indigo-100 text-indigo-800 border border-indigo-200" },
                    manufacturer: { label: "Manufacturer (likely source of backorder)", cls: "bg-amber-100 text-amber-900 border border-amber-300" },
                    marketplace:  { label: "Marketplace",                            cls: "bg-slate-100 text-slate-600 border border-slate-200" },
                  }[lead.vendor_type] || null;
                  return (
                    <tr key={lead.id} className={`${selectedIds.has(lead.id) ? "bg-cyan-50/60" : ""} ${isManufacturer ? "opacity-70" : ""}`}>
                      <td className="p-2 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(lead.id)}
                          onChange={() => toggleOne(lead.id)}
                          disabled={!hasEmail}
                          data-testid={`discovery-row-${lead.id}`}
                          title={isManufacturer ? "Manufacturer — usually NOT helpful when they're the source of the backorder" : ""}
                        />
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                          {lead.company_name || "Unknown supplier"}
                          {lead.is_verified_supplier && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title="Already a verified Sterile Mate supplier">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Verified
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">{lead.sku && `SKU: ${lead.sku}`}</div>
                        {lead.ai_notes && (
                          <div className="text-[10px] text-slate-400 italic mt-0.5 max-w-[260px]">{lead.ai_notes}</div>
                        )}
                        {lead.converted_supplier_id ? (
                          <div className="mt-1 text-[10px] font-semibold text-emerald-700 leading-tight">
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Added to suppliers
                            </span>
                            {lead.intro_sent_at && (
                              <span className="text-slate-500 font-normal"> · intro sent</span>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => setIntroLead(lead)}
                            disabled={!hasEmail}
                            title={hasEmail ? "Add to the supplier list and introduce ourselves" : "No email captured for this lead"}
                            className="mt-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-cyan-300 text-cyan-800 bg-white hover:bg-cyan-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 whitespace-nowrap"
                            data-testid={`discovery-add-supplier-${lead.id}`}
                          >
                            <UserPlus className="w-3 h-3" /> Add supplier
                          </button>
                        )}
                      </td>
                      <td className="p-2 align-top space-y-1">
                        {vendorPill && (
                          <div>
                            <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${vendorPill.cls}`}>
                              {vendorPill.label}
                            </span>
                          </div>
                        )}
                        {aiPill && (
                          <div>
                            <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${aiPill.cls}`}>
                              {aiPill.label}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800">
                            {lead.status_label === "possible_source" ? "Possible source" : "Uncertain"}
                          </span>
                        </div>
                        {lead.contacted && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700">
                            <CheckCircle2 className="w-3 h-3" /> Contacted
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top space-y-0.5">
                        {(lead.emails || []).map(e => (
                          <div key={e} className="text-[11px] text-slate-700 flex items-center gap-1">
                            <Mail className="w-3 h-3 text-slate-400" /> {e}
                          </div>
                        ))}
                        {(lead.phones || []).map(p => (
                          <div key={p} className="text-[11px] text-slate-700 flex items-center gap-1">
                            <Phone className="w-3 h-3 text-slate-400" /> {p}
                          </div>
                        ))}
                        {!hasEmail && (lead.phones || []).length === 0 && (
                          <span className="text-[11px] text-slate-400 italic">No contact found</span>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        <a
                          href={lead.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:underline"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
            <div className="text-xs text-slate-500">
              {leads.length} possible {leads.length === 1 ? "supplier" : "suppliers"} found · {totalEmails} email{totalEmails === 1 ? "" : "s"} captured · {selectedIds.size} selected
            </div>
            <button
              onClick={contactSelected}
              disabled={contacting || selectedIds.size === 0}
              className="text-sm font-semibold px-4 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-700 disabled:bg-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="discovery-send-email"
            >
              <Mail className="w-4 h-4" />
              {contacting ? "Building email…" : `Send Sourcing Email to Selected (${selectedIds.size})`}
            </button>
          </div>
        </>
      )}

      <SupplierIntroDialog
        key={introLead?.id || "none"}
        open={!!introLead}
        onClose={() => setIntroLead(null)}
        token={token}
        lead={introLead}
        onDone={fetchLeads}
      />
    </section>
  );
}
